import { MiaPlaza } from "@/Reinforced.Typings";
import { Action, Getter, Mutation, State } from "vuex-simple";
import { IntroJs } from "intro.js/src/intro";
import introJs from 'intro.js';
import { IntroStep, ScrollTo, TooltipPosition } from "intro.js/src/core/steps";
import I18Next from "../I18Next";
import Log from "@/utils/Log";
import axios from "axios";
import { Options } from "intro.js/src/option";
import { cloneDeep, noop } from 'lodash';
import { isElementVisible } from "@/utils/PageReadinessDetector";

const NOTIFIER_ELEM = '#mainContent > .mia-Notifier';
const TOUR_STEP_VALIDATION_TIMEOUT_MS = 2000;
const TOUR_AUTOSTART_DELAY_MS = 1000;

/**
 * Manages guided tours using Intro.js, including initialization, destruction, state management, 
 * dynamic step handling, DOM validation, and API interactions for recording tour progress. 
 * Supports triggers and TRs.
 * 
 * ---
 * 
 * ### What is a MiaTour?
 * MiaTours are interactive guided walkthroughs that help users learn how to use various features
 * of the platform. Each tour consists of a series of steps that highlight UI elements and provide
 * explanatory tooltips.
 * 
 * ### Core Responsibilities
 * - Initialize and manage intro.js instances
 * - Track the current state of active tours
 * - Validate DOM elements targeted by tour steps
 * - Handle step navigation and tour completion
 * - Persist tour completion state to the backend
 * - Support automatic triggering based on conditions
 * 
 * ### Key Methods
 * - `InitializeTour`: Prepares a tour for display
 * - `StartTour`: Begins an initialized tour
 * - `validateSteps`: Ensures all required elements exist in the DOM
 * - `loadTourTextResources`: Handles i18n for tour content
 * 
 * ### Related Components
 * - `MiaTour.ts`: Vue component wrapper for initializing tours
 * - `MiaTourStep.vue`: Component to render tour step content
 * - `FloatingHelpButton.vue`: UI for accessing tours
 * 
 * ### Translation
 * Tour text is stored as TRs with keys following the pattern:
 * `MiaTour_{TourName}_Step{StepIndex}`
 */
export default class MiaTourModule {
	private readonly tours!: Record<MiaPlaza.Model.MiaTour.Tour, MiaPlaza.LO.MiaTours.IMiaTour>;
	private readonly firstLogin!: boolean;
	private introInstance?: IntroJs;
	private stepsDictionary: { [key: number]: number } = {};

	@State()
	private memberTours!: MiaPlaza.Model.MiaTour.Tour[];
	@State()
	private currentStep: number | null;
	@State()
	private tour: MiaPlaza.Model.MiaTour.Tour | null = null;

	/**
	 * Creates a new instance of the MiaTourModule.
	 * @param tours - Dictionary of all available tours, keyed by tour enum value
	 * @param memberTours - List of tours that the current member has already completed
	 * @param firstLogin - Whether the current user is considered to be in their first login period
	 */
	constructor(tours: Record<MiaPlaza.Model.MiaTour.Tour, MiaPlaza.LO.MiaTours.IMiaTour>, memberTours: MiaPlaza.Model.MiaTour.Tour[], firstLogin: boolean) {
		this.tours = tours;
		this.memberTours = memberTours;
		this.firstLogin = firstLogin;
		this.currentStep = null;
	}

	@Getter()
	public get Tour(): MiaPlaza.Model.MiaTour.Tour | null {
		return this.tour;
	}

	@Getter()
	public get HasActiveTour(): boolean {
		if (!this.tour || !this.tours[this.tour]) {
			return false;
		}

		const tourObj = this.tours[this.tour];

		return tourObj.Steps.length > 0;
	}

	@Getter()
	public get IsTourVisible(): boolean {
		return this.HasActiveTour && !!this.introInstance && this.currentStep !== null;
	}

	@Getter()
	public get CurrentStepTR(): string | null {
		return this.HasActiveTour && this.currentStep !== null
			? this.getStepTextResourceKey(this.currentStep)
			: null;
	}

	private get isPreviouslyTriggeredTour(): boolean {
		return this.memberTours.some((tour) => tour === this.tour);
	}

	private get areTourTriggersMet(): boolean {
		if (!this.HasActiveTour) {
			return false;
		}
	
		const tour = this.tours[this.tour as MiaPlaza.Model.MiaTour.Tour];
	
		const triggerChecks: Record<MiaPlaza.LO.MiaTours.TourTrigger, boolean> = {
			[MiaPlaza.LO.MiaTours.TourTrigger.FirstLogin]: this.firstLogin
		};
	
		return tour.Triggers.every(trigger => 
			trigger in triggerChecks ? triggerChecks[trigger] : (void Log.error(`MiaTourModule: Unrecognized trigger "${trigger}".`), false)
		);
	}
	

	@Mutation()
	public InitializeTour(tour: MiaPlaza.Model.MiaTour.Tour, options?: Partial<Options>): void {
		if (this.tour) {
			void Log.error(`MiaTourModule: Cannot initialize tour "${String(tour)}" as tour "${String(this.tour)}" is already active.`);
			return;
		}

		this.tour = this.tours[tour] ? tour : null;
		if (!this.HasActiveTour) {
			return void Log.error(`MiaTourModule: Tour with ID "${String(tour)}" not found or inactive.`);
		}

		this.setupIntroInstance(options);
		if (this.areTourTriggersMet && !this.isPreviouslyTriggeredTour) {
			void this.StartTour(true);
		}
	}

	@Mutation()
	public DestroyTour(tour: MiaPlaza.Model.MiaTour.Tour): void {
		if (!this.tour) {
			return;
		}

		if (this.tour !== tour) {
			void Log.error(`MiaTourModule: Cannot destroy tour "${String(tour)}". Active tour ID is "${String(this.tour)}".`);
			return;
		}

		this.cleanupIntroInstance();
		this.resetTourState();
	}

	private setupIntroInstance(options?: Partial<Options>): void {
		this.introInstance = introJs();
		this.introInstance.onafterchange(() => this.afterChangeCallback());
		this.introInstance.onexit(() => this.exitCallback());

		if (options) {
			this.introInstance.setOptions(options);
		}
	}

	private cleanupIntroInstance(): void {
		if (this.introInstance) {
			this.introInstance.onexit(noop);
			this.introInstance.onafterchange(noop);
			void this.introInstance.exit(false);
		}
		this.introInstance = undefined;
	}

	private resetTourState(): void {
		this.tour = null;
		this.currentStep = null;
	}

	@Action()
	public async StartTour(autoStart = false): Promise<void> {
		if (!this.HasActiveTour || !this.introInstance) {
			throw new Error(`MiaTourModule: Unable to start the tour. No active tour is currently initialized.`);
		}

		this.loadTourTextResources();
		const startTime = performance.now();
		const addedSteps = await this.addStepsToIntroInstance(this.tours[this.tour as MiaPlaza.Model.MiaTour.Tour].Steps);
		if (!addedSteps.length) {
			return;
		}

		const stepInitializationDuration = performance.now() - startTime;

		if (autoStart && stepInitializationDuration < TOUR_AUTOSTART_DELAY_MS) {
			await new Promise(resolve => setTimeout(resolve, TOUR_AUTOSTART_DELAY_MS - stepInitializationDuration));
		}

		jQuery(NOTIFIER_ELEM).hide();
		void this.introInstance.start();
	}

	private afterChangeCallback(): void {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		this.currentStep = this.stepsDictionary[this.introInstance!.currentStep()];
	}

	private exitCallback(): void {
		jQuery(NOTIFIER_ELEM).show();
		this.currentStep = null;
		if (!this.isPreviouslyTriggeredTour && this.tour) {
			this.memberTours.push(this.tour);
			void axios.get(MiaPlaza.Controllers.MiaTours.MemberTourController.GetCreateMemberTourUrl(this.tour));
		}
	}

	private async addStepsToIntroInstance(steps: MiaPlaza.LO.MiaTours.IMiaTourStep[]): Promise<Partial<IntroStep>[]> {
		const validatedSteps = await this.validateSteps(cloneDeep(steps));

		const introSteps = this.mapStepsToIntroFormat(validatedSteps);

		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		this.introInstance!.setOptions({ steps: introSteps });
		return introSteps;
	}

	/**
	 * Validates and filters tour steps based on their presence and visibility in the DOM.
	 * - Uses a `MutationObserver` to detect dynamically added elements.
	 * - Resolves immediately if all steps are valid; otherwise, waits up to the specified timeout.
	 * - If required steps are still missing after the timeout, logs an error and returns an empty array.
	 * - Filters out elements that are not visible or contain only '…' text.
	 */
	private async validateSteps(steps: MiaPlaza.LO.MiaTours.IMiaTourStep[]): Promise<MiaPlaza.LO.MiaTours.IMiaTourStep[]> {
		const getValidSteps = (): MiaPlaza.LO.MiaTours.IMiaTourStep[] => {
			this.stepsDictionary = {};
			let validStepIndex = 0;

			return steps.filter((step, index) => {
				const selector = step.Selector;

				if (typeof selector === 'string') {
					const foundElements = Array.from(document.querySelectorAll<HTMLElement>(selector));
					const firstVisibleElement = foundElements.find(isElementVisible);

					if (!firstVisibleElement) {
						return false;
					}

					// Skip elements that only contain '…' as they are likely TRs that have not been loaded yet
					if (firstVisibleElement.textContent && firstVisibleElement.textContent.trim() === '…') {
						return false;
					}

					step.Selector = firstVisibleElement;
				} else if (selector instanceof HTMLElement && !document.body.contains(selector)) {
					return false;
				}

				this.stepsDictionary[validStepIndex++] = index;
				return true;
			});
		};

		return new Promise<MiaPlaza.LO.MiaTours.IMiaTourStep[]>(resolve => {
			let validSteps = getValidSteps();
			if (validSteps.length === steps.length) {
				return resolve(validSteps);
			}

			const observer = new MutationObserver(() => {
				validSteps = getValidSteps();
				if (validSteps.length === steps.length) {
					observer.disconnect();
					resolve(validSteps);
				}
			});
	
			observer.observe(document.body, { childList: true, subtree: true });

			// A `setTimeout` is used to:
			// - Ensure we don't wait forever if an element never appears
			// - Disconnect the observer after the specified timeout and resolve with whatever steps were found
			// - Prevent an infinite loop while still allowing dynamically loaded elements to be detected
			setTimeout(() => {
				observer.disconnect();
				resolve(validSteps);
			}, TOUR_STEP_VALIDATION_TIMEOUT_MS);
		}).then(async (validSteps) => {
			const missingSteps = steps.filter(step => !step.Optional && !validSteps.includes(step));
			if (missingSteps.length > 0) {
				void Log.error(`MiaTourModule: Required step with element "${missingSteps[0].Selector as string}" is missing in the DOM. Active Tour ID is "${String(this.tour) ?? "None"}".`);
				return [];
			}

			return validSteps;
		});
	}

	private mapStepsToIntroFormat(steps: MiaPlaza.LO.MiaTours.IMiaTourStep[]): Partial<IntroStep>[] {
		return steps.map(step => ({
			element: step.Selector,
			disableInteraction: step.DisableInteraction,
			position: step.Position as TooltipPosition,
			scrollTo: step.ScrollTo as ScrollTo,
			title: step.Heading,
		}));
	}

	private loadTourTextResources(): void {
		this.tours[this.tour as MiaPlaza.Model.MiaTour.Tour].Steps.map((step: MiaPlaza.LO.MiaTours.IMiaTourStep, index: number) => {
			void I18Next.tr(this.getStepTextResourceKey(index));
		});
	}

	private getStepTextResourceKey(index: number): string {
		return `MiaTour_${String(this.tour)}_content_${index}`;
	}
}
